import React, { useEffect, useRef, useState } from "react";

const C = {
  bg: "#0A0908",
  panel: "#120F0C",
  panelLight: "#1A1510",
  border: "#27201A",
  borderBright: "#3A2E22",
  cheddar: "#F5A524",
  cheddarBright: "#FFD166",
  cheddarDim: "#8A5E16",
  cream: "#F5F1E8",
  muted: "#6B5F50",
  mutedBright: "#9A8A76",
  buy: "#4ADE80",
  sell: "#EF4444",
};

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SERIF = "'Instrument Serif', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";

// ============================================================
// Access gate
// ------------------------------------------------------------
// Email + 6-digit code, verified server-side by Pages Functions
// (/api/request-code, /api/verify-code). Session is a signed
// HttpOnly cookie — nothing secret ships in this bundle.
// ============================================================

// Bump this when legal docs are revised — forces re-acceptance.
const LEGAL_DOC_VERSION = "2026-06-01";
const LEGAL_ACCEPT_KEY = "sc-legal-accept";

const LEGAL_DOCS = {
  terms:      { title: "Terms of Service",  path: "/legal/terms.md" },
  risk:       { title: "Risk Disclosure",   path: "/legal/risk.md" },
  disclaimer: { title: "Disclaimer",        path: "/legal/disclaimer.md" },
};

function hasAcceptedLegal() {
  try {
    const v = JSON.parse(localStorage.getItem(LEGAL_ACCEPT_KEY) || "null");
    return v?.version === LEGAL_DOC_VERSION;
  } catch {
    return false;
  }
}

function recordLegalAcceptance() {
  try {
    localStorage.setItem(
      LEGAL_ACCEPT_KEY,
      JSON.stringify({
        version: LEGAL_DOC_VERSION,
        acceptedAt: new Date().toISOString(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      })
    );
  } catch {}
}

const TICKERS = [
  { sym: "BONK", px: "0.00002847", chg: 12.4 },
  { sym: "WIF", px: "2.3451", chg: -4.2 },
  { sym: "POPCAT", px: "0.8923", chg: 8.7 },
  { sym: "MEW", px: "0.00412", chg: -2.1 },
  { sym: "BOME", px: "0.00891", chg: 22.3 },
  { sym: "SLERF", px: "0.2104", chg: -6.8 },
  { sym: "MICHI", px: "0.3245", chg: 15.1 },
  { sym: "PNUT", px: "0.7821", chg: 3.4 },
  { sym: "GOAT", px: "0.4512", chg: -1.9 },
  { sym: "FWOG", px: "0.0632", chg: 44.2 },
  { sym: "MOODENG", px: "0.1823", chg: -9.3 },
  { sym: "CHILLGUY", px: "0.2891", chg: 6.7 },
];

function FootprintCanvas() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({ t: 0, bars: [] });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const NUM_BARS = 28;
    const ROWS = 14;
    const bars = [];
    for (let i = 0; i < NUM_BARS; i++) {
      const row = [];
      const midRow = ROWS / 2 + Math.sin(i * 0.3) * 3;
      for (let r = 0; r < ROWS; r++) {
        const dist = Math.abs(r - midRow);
        const base = Math.max(0, 1 - dist / (ROWS * 0.5));
        const buy = base * (0.4 + Math.random() * 0.6) * (Math.random() > 0.3 ? 1 : 0.2);
        const sell = base * (0.4 + Math.random() * 0.6) * (Math.random() > 0.3 ? 1 : 0.2);
        row.push({ buy, sell });
      }
      bars.push({ rows: row });
    }
    stateRef.current.bars = bars;

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;
      ctx.clearRect(0, 0, W, H);

      ctx.strokeStyle = "rgba(245, 165, 36, 0.04)";
      ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 40) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = 0; y < H; y += 30) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      const barW = W / (NUM_BARS + 2);
      const rowH = (H - 40) / ROWS;
      const t = stateRef.current.t;

      bars.forEach((bar, i) => {
        const x = barW * (i + 1);
        const reveal = Math.min(1, Math.max(0, (t - i * 2) / 30));
        if (reveal <= 0) return;

        bar.rows.forEach((cell, r) => {
          const y = 20 + r * rowH;
          const cellW = barW * 0.92;
          const cellH = rowH * 0.92;

          if (cell.buy > 0.1) {
            ctx.fillStyle = `rgba(74, 222, 128, ${cell.buy * reveal * 0.85})`;
            ctx.fillRect(x, y, cellW / 2, cellH);
          }
          if (cell.sell > 0.1) {
            ctx.fillStyle = `rgba(239, 68, 68, ${cell.sell * reveal * 0.85})`;
            ctx.fillRect(x + cellW / 2, y, cellW / 2, cellH);
          }
          if ((cell.buy + cell.sell) > 1.4) {
            ctx.strokeStyle = `rgba(245, 165, 36, ${reveal * 0.9})`;
            ctx.lineWidth = 1;
            ctx.strokeRect(x, y, cellW, cellH);
          }
        });
      });

      const sweepX = ((t * 1.2) % (W + 100)) - 50;
      const grad = ctx.createLinearGradient(sweepX - 40, 0, sweepX + 40, 0);
      grad.addColorStop(0, "rgba(255, 209, 102, 0)");
      grad.addColorStop(0.5, "rgba(255, 209, 102, 0.25)");
      grad.addColorStop(1, "rgba(255, 209, 102, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(sweepX - 40, 0, 80, H);

      stateRef.current.t += 1;
      if (!reducedMotion) rafRef.current = requestAnimationFrame(draw);
    };
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reducedMotion) stateRef.current.t = NUM_BARS * 2 + 30; // full reveal, one static frame
    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

// ============================================================
// Roulette walk — candlesticks built from red/black spins.
// Black = +1 tick, red = −1, zero = flat. Pure random walk;
// rendered as the "no edge" contrast to reading real orderflow.
// ============================================================
const ROULETTE_REDS = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);

function RouletteCanvas() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const SPINS_PER_CANDLE = 20;
    const MAX_CANDLES = 36;
    const MA_PERIOD = 9;
    const MA_WARMUP = MA_PERIOD - 1;        // hidden lead-in candles (off-screen left)
    const BUF_CANDLES = MAX_CANDLES + MA_WARMUP;
    const st = {
      price: 0,
      candles: [],
      cur: null, // { open, high, low, close, n }
      lastSpin: 0,
      counts: { red: 0, black: 0, zero: 0 },
      lastTime: 0,
    };
    stRef.current = st;

    const spin = () => {
      const n = Math.floor(Math.random() * 37);
      const t = n === 0 ? 0 : ROULETTE_REDS.has(n) ? -1 : 1;
      if (n === 0) st.counts.zero++;
      else if (t > 0) st.counts.black++;
      else st.counts.red++;
      st.price += t;
      st.lastSpin = n;
      if (!st.cur) st.cur = { open: st.price - t, high: st.price - t, low: st.price - t, close: st.price, n: 0 };
      st.cur.close = st.price;
      if (st.price > st.cur.high) st.cur.high = st.price;
      if (st.price < st.cur.low) st.cur.low = st.price;
      st.cur.n++;
      if (st.cur.n >= SPINS_PER_CANDLE) {
        st.candles.push(st.cur);
        if (st.candles.length > BUF_CANDLES) st.candles.shift();
        st.cur = null;
      }
    };

    // seed history
    for (let i = 0; i < BUF_CANDLES * SPINS_PER_CANDLE; i++) spin();

    const draw = (now) => {
      if (now - st.lastTime > 420) { spin(); st.lastTime = now; }

      const rect = canvas.getBoundingClientRect();
      const W = rect.width, H = rect.height;
      ctx.clearRect(0, 0, W, H);

      const all = st.cur ? [...st.candles, st.cur] : st.candles;
      if (!all.length) { rafRef.current = requestAnimationFrame(draw); return; }

      let lo = Infinity, hi = -Infinity;
      for (let i = MA_WARMUP; i < all.length; i++) {
        const c = all[i];
        if (c.low < lo) lo = c.low; if (c.high > hi) hi = c.high;
      }
      const pad = Math.max(2, (hi - lo) * 0.12);
      lo -= pad; hi += pad;
      const y = (v) => H - 26 - ((v - lo) / (hi - lo)) * (H - 44);

      // faint grid
      ctx.strokeStyle = "rgba(245, 165, 36, 0.05)";
      ctx.lineWidth = 1;
      for (let gy = 14; gy < H - 26; gy += 24) {
        ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke();
      }

      // +2 slots: one empty column of space at the right edge
      const slot = W / (MAX_CANDLES + 2);
      const bw = Math.max(2, slot * 0.55);

      all.forEach((c, i) => {
        const cx = slot * (i - MA_WARMUP + 0.5);
        const up = c.close >= c.open;
        const col = up ? C.buy : C.sell;
        ctx.strokeStyle = col;
        ctx.fillStyle = col;
        ctx.lineWidth = 1.2;
        ctx.beginPath(); ctx.moveTo(cx, y(c.high)); ctx.lineTo(cx, y(c.low)); ctx.stroke();
        const yo = y(c.open), yc = y(c.close);
        const top = Math.min(yo, yc), bh = Math.max(2, Math.abs(yc - yo));
        ctx.globalAlpha = c === st.cur ? 0.75 : 1;
        ctx.fillRect(cx - bw / 2, top, bw, bh);
        ctx.globalAlpha = 1;
      });

      // MA9 over candle closes — the provocation. A moving average on pure
      // coin flips reads as trend confirmation; that's the whole argument.
      if (all.length >= MA_PERIOD) {
        ctx.strokeStyle = C.cheddarBright;
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        let started = false;
        for (let i = MA_PERIOD - 1; i < all.length; i++) {
          let s = 0;
          for (let k = i - (MA_PERIOD - 1); k <= i; k++) s += all[k].close;
          const mx = slot * (i - MA_WARMUP + 0.5);
          const my = y(s / MA_PERIOD);
          if (!started) {
            // Start one slot off-screen left so the line enters from the
            // edge instead of beginning just inside it.
            ctx.moveTo(mx - slot, my);
            ctx.lineTo(mx, my);
            started = true;
          } else ctx.lineTo(mx, my);
        }
        ctx.stroke();
      }

      // last-price line
      const ly = y(st.price);
      ctx.strokeStyle = "rgba(245, 165, 36, 0.35)";
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(0, ly); ctx.lineTo(W, ly); ctx.stroke();
      ctx.setLineDash([]);

      // footer readout
      ctx.font = `10px ${"'JetBrains Mono', monospace"}`;
      ctx.fillStyle = C.muted;
      const net = st.counts.black - st.counts.red;
      const spinCol = st.lastSpin === 0 ? C.buy : ROULETTE_REDS.has(st.lastSpin) ? C.sell : C.cream;
      ctx.fillText(`SPIN`, 8, H - 10);
      ctx.fillStyle = spinCol;
      ctx.fillText(String(st.lastSpin).padStart(2, "0"), 44, H - 10);
      ctx.fillStyle = C.muted;
      ctx.fillText(`B ${st.counts.black}  R ${st.counts.red}  0 ${st.counts.zero}  NET ${net > 0 ? "+" : ""}${net}`, 76, H - 10);

      if (!reducedMotion) rafRef.current = requestAnimationFrame(draw);
    };
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reducedMotion) draw(0);
    else rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

function TickerTape() {
  const doubled = [...TICKERS, ...TICKERS];
  return (
    <div
      style={{
        borderTop: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        background: `linear-gradient(90deg, ${C.bg} 0%, ${C.panel} 50%, ${C.bg} 100%)`,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div className="sc-ticker-track">
        {doubled.map((t, i) => (
          <div key={i} className="sc-ticker-item" style={{ fontFamily: MONO }}>
            <span style={{ color: C.mutedBright }}>{t.sym}</span>
            <span style={{ color: C.cream }}>${t.px}</span>
            <span style={{ color: t.chg >= 0 ? C.buy : C.sell }}>
              {t.chg >= 0 ? "▲" : "▼"} {Math.abs(t.chg).toFixed(2)}%
            </span>
            <span style={{ color: C.border }}>│</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TerminalBar() {
  const [clock, setClock] = useState("");
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setClock(d.toISOString().slice(0, 19).replace("T", " ") + " UTC");
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="sc-term-bar"
      style={{
        borderBottom: `1px solid ${C.border}`,
        background: C.panel,
        fontFamily: MONO,
        color: C.mutedBright,
        letterSpacing: "0.05em",
        fontSize: 11,
      }}
    >
      <div className="sc-term-left">
        <div className="sc-term-group">
          <span className="sc-pulse-dot" style={{ background: C.buy }} />
          <span style={{ color: C.cream }}>GRPC://NLN</span>
          <span>STREAMING</span>
        </div>
        <div className="sc-term-md sc-term-group">
          <span style={{ color: C.muted }}>SOL</span>
          <span style={{ color: C.cheddar }}>$241.87</span>
          <span style={{ color: C.buy }}>+2.14%</span>
        </div>
        <div className="sc-term-lg sc-term-group">
          <span style={{ color: C.muted }}>BLOCK</span>
          <span style={{ color: C.cream }}>298,441,029</span>
        </div>
      </div>
      <div className="sc-term-right">
        <span className="sc-term-sm" style={{ color: C.muted }}>{clock}</span>
        <span style={{ color: C.cheddar }}>v2.4.1</span>
      </div>
    </div>
  );
}

function FeatureCard({ id, title, tag, desc, metric, metricLabel, accent }) {
  return (
    <div className="sc-feature-card">
      <div className="sc-corner sc-corner-tl" />
      <div className="sc-corner sc-corner-tr" />
      <div className="sc-corner sc-corner-bl" />
      <div className="sc-corner sc-corner-br" />

      <div className="sc-feature-head">
        <div className="sc-feature-tag" style={{ fontFamily: MONO, color: C.muted }}>
          [{id}] {tag}
        </div>
        <div className="sc-feature-dot" style={{ background: accent }} />
      </div>

      <h3 className="sc-feature-title" style={{ fontFamily: SERIF, color: C.cream }}>
        {title}
      </h3>

      <p className="sc-feature-desc" style={{ color: C.mutedBright, fontFamily: SANS }}>
        {desc}
      </p>

      <div className="sc-feature-foot" style={{ borderTop: `1px solid ${C.border}` }}>
        <div>
          <div className="sc-metric-label" style={{ color: C.muted, fontFamily: MONO }}>
            {metricLabel}
          </div>
          <div className="sc-metric" style={{ color: C.cheddarBright, fontFamily: MONO }}>
            {metric}
          </div>
        </div>
        <div className="sc-feature-arrow" style={{ color: C.cheddar, fontFamily: MONO }}>→</div>
      </div>
    </div>
  );
}

// ============================================================
// Inline-markdown renderer (bold, italic only)
// ============================================================
function renderInline(text) {
  const out = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let last = 0;
  let key = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={key++}>{m[1]}</strong>);
    else out.push(<em key={key++}>{m[2]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : text;
}

// ============================================================
// Block-level markdown renderer (lightweight, no deps)
// Handles: # ## ### headers, > blockquote, - lists, --- hr, **bold**, *italic*
// ============================================================
function MarkdownLite({ text }) {
  const blocks = text.split(/\n\n+/);
  return (
    <>
      {blocks.map((raw, i) => {
        const block = raw.trim();
        if (!block) return null;
        if (block === "---") return <hr key={i} className="sc-legal-hr" />;
        if (block.startsWith("# "))   return <h1 key={i} className="sc-legal-h1">{renderInline(block.slice(2))}</h1>;
        if (block.startsWith("## "))  return <h2 key={i} className="sc-legal-h2">{renderInline(block.slice(3))}</h2>;
        if (block.startsWith("### ")) return <h3 key={i} className="sc-legal-h3">{renderInline(block.slice(4))}</h3>;
        if (block.startsWith("> ")) {
          const content = block.split("\n").map((l) => l.replace(/^>\s?/, "")).join(" ");
          return <blockquote key={i} className="sc-legal-quote">{renderInline(content)}</blockquote>;
        }
        if (/^[-*]\s/.test(block)) {
          const items = block.split("\n").filter((l) => /^[-*]\s/.test(l)).map((l) => l.replace(/^[-*]\s/, ""));
          return (
            <ul key={i} className="sc-legal-ul">
              {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
            </ul>
          );
        }
        return <p key={i} className="sc-legal-p">{renderInline(block.replace(/\n/g, " "))}</p>;
      })}
    </>
  );
}

// ============================================================
// Legal document modal — fetches markdown from /public/legal/
// ============================================================
function LegalModal({ docKey, onClose }) {
  const meta = LEGAL_DOCS[docKey];
  const [content, setContent] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!meta) return;
    let alive = true;
    setContent(null);
    setErr(null);
    fetch(meta.path)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => alive && setContent(t))
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [meta]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!meta) return null;

  return (
    <div className="sc-modal-backdrop" onClick={onClose}>
      <div className="sc-modal sc-modal-legal" onClick={(e) => e.stopPropagation()}>
        <div className="sc-modal-head">
          <span style={{ color: C.cheddar }}>◆</span>
          <span style={{ color: C.cream, fontFamily: MONO, letterSpacing: "0.15em", fontSize: 12 }}>
            {meta.title.toUpperCase()}
          </span>
          <span style={{ flex: 1 }} />
          <button className="sc-modal-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sc-modal-body">
          {err && <p style={{ color: C.sell }}>Failed to load: {err}</p>}
          {!err && content === null && <p style={{ color: C.muted }}>Loading...</p>}
          {!err && content !== null && <MarkdownLite text={content} />}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Access gate — email → 6-digit code → session cookie.
// Not on the allowlist → offer the waitlist.
// Legal acceptance required before code verification.
// ============================================================
function AccessGate({ onSuccess, onClose, openLegal }) {
  const [step, setStep] = useState("email"); // email | code | waitlist | waitlist-done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [accepted, setAccepted] = useState(hasAcceptedLegal());
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const post = async (path, body) => {
    const res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Something went wrong.");
    return data;
  };

  const requestCode = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setErr("");
    setBusy(true);
    try {
      const data = await post("/api/request-code", { email });
      if (data.allowed) {
        setStep("code");
        setCode("");
        setCooldown(30);
      } else {
        setStep("waitlist");
      }
    } catch (ex) {
      setErr(ex.message);
    }
    setBusy(false);
  };

  const verifyCode = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!accepted) { setErr("You must agree to the Terms, Risk Disclosure, and Disclaimer."); return; }
    setErr("");
    setBusy(true);
    try {
      await post("/api/verify-code", { email, code });
      recordLegalAcceptance();
      onSuccess();
    } catch (ex) {
      setErr(ex.message);
    }
    setBusy(false);
  };

  const joinWaitlist = async () => {
    if (busy) return;
    setErr("");
    setBusy(true);
    try {
      const data = await post("/api/waitlist", { email });
      if (data.status === "already_allowed") {
        // Edge case: they got approved between checks — send them back through
        setStep("email");
        setErr("");
      } else {
        setStep("waitlist-done");
      }
    } catch (ex) {
      setErr(ex.message);
    }
    setBusy(false);
  };

  return (
    <div className="sc-modal-backdrop" onClick={onClose}>
      <div className="sc-modal sc-modal-gate" onClick={(e) => e.stopPropagation()}>
        <div className="sc-modal-head">
          <span className="sc-pulse-dot" style={{ background: C.cheddar }} />
          <span style={{ color: C.cream, fontFamily: MONO, letterSpacing: "0.2em", fontSize: 12 }}>
            {step === "waitlist" || step === "waitlist-done" ? "WAITLIST" : "ACCESS REQUIRED"}
          </span>
          <span style={{ flex: 1 }} />
          <button className="sc-modal-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        {step === "email" && (
          <form className="sc-gate-body" onSubmit={requestCode}>
            <p className="sc-gate-lede">
              Cheddar Terminal is in private beta. Enter your email and we'll send you an access code.
              <span style={{ display: 'block', marginTop: 4, opacity: 0.55, fontStyle: 'italic', fontSize: '0.9em' }}>
                Cheddar Terminal está en beta privada. Ingresa tu correo y te enviaremos un código de acceso.
              </span>
            </p>
            <label className="sc-gate-label">EMAIL</label>
            <input
              ref={inputRef}
              type="email"
              className="sc-gate-input"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (err) setErr(""); }}
              placeholder="you@example.com"
              autoComplete="email"
              spellCheck={false}
            />
            {err && <div className="sc-gate-err">{err}</div>}
            <button type="submit" className="sc-btn sc-btn-primary" disabled={busy} style={{ width: "100%", marginTop: 8 }}>
              {busy ? "CHECKING..." : "SEND CODE →"}
            </button>
            <div className="sc-gate-foot">
              <span>Trading involves substantial risk of loss.</span>
            </div>
          </form>
        )}

        {step === "code" && (
          <form className="sc-gate-body" onSubmit={verifyCode}>
            <p className="sc-gate-lede">
              Code sent to <span style={{ color: C.cheddarBright }}>{email}</span>. It expires in 10 minutes.
            </p>
            <label className="sc-gate-label">6-DIGIT CODE</label>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="sc-gate-input sc-gate-code"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); if (err) setErr(""); }}
              autoComplete="one-time-code"
              spellCheck={false}
            />

            <label className="sc-gate-check">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => { setAccepted(e.target.checked); if (err) setErr(""); }}
              />
              <span>
                I have read and agree to the{" "}
                <button type="button" className="sc-gate-link" onClick={() => openLegal("terms")}>Terms of Service</button>,{" "}
                <button type="button" className="sc-gate-link" onClick={() => openLegal("risk")}>Risk Disclosure</button>, and{" "}
                <button type="button" className="sc-gate-link" onClick={() => openLegal("disclaimer")}>Disclaimer</button>.
              </span>
            </label>

            {err && <div className="sc-gate-err">{err}</div>}

            <button type="submit" className="sc-btn sc-btn-primary" disabled={busy} style={{ width: "100%", marginTop: 8 }}>
              {busy ? "VERIFYING..." : "ENTER TERMINAL →"}
            </button>

            <div className="sc-gate-links">
              <button type="button" className="sc-gate-link" disabled={cooldown > 0 || busy} onClick={requestCode}>
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
              </button>
              <button type="button" className="sc-gate-link" onClick={() => { setStep("email"); setErr(""); }}>
                Change email
              </button>
            </div>
          </form>
        )}

        {step === "waitlist" && (
          <div className="sc-gate-body">
            <p className="sc-gate-lede">
              <span style={{ color: C.cheddarBright }}>{email}</span> isn't on the beta list yet.
              Join the waitlist and you'll get an access code when a spot opens up.
            </p>
            {err && <div className="sc-gate-err">{err}</div>}
            <button className="sc-btn sc-btn-primary" disabled={busy} onClick={joinWaitlist} style={{ width: "100%", marginTop: 8 }}>
              {busy ? "JOINING..." : "JOIN WAITLIST →"}
            </button>
            <div className="sc-gate-links">
              <button type="button" className="sc-gate-link" onClick={() => { setStep("email"); setErr(""); }}>
                Use a different email
              </button>
            </div>
          </div>
        )}

        {step === "waitlist-done" && (
          <div className="sc-gate-body">
            <p className="sc-gate-lede" style={{ textAlign: "center", padding: "12px 0" }}>
              <span style={{ color: C.buy, fontFamily: MONO, letterSpacing: "0.15em", display: "block", marginBottom: 12 }}>
                ● YOU'RE ON THE LIST
              </span>
              We'll email <span style={{ color: C.cheddarBright }}>{email}</span> when your spot opens.
            </p>
            <button className="sc-btn sc-btn-primary" onClick={onClose} style={{ width: "100%" }}>
              BACK TO SITE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SolCheddarWelcome({ onEnter = () => {} }) {
  const [gateOpen, setGateOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null); // "terms" | "risk" | "disclaimer"
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    document.body.classList.add("sc-welcome-active");
    // Existing 30-day session? Skip the gate on ENTER.
    fetch("/api/me")
      .then((r) => r.json())
      .then((d) => setAuthed(!!d.authed))
      .catch(() => {});
    return () => document.body.classList.remove("sc-welcome-active");
  }, []);

  const handleEnterClick = () => {
    if (authed && hasAcceptedLegal()) onEnter();
    else setGateOpen(true);
  };
  const handleGateSuccess = () => { setGateOpen(false); setAuthed(true); onEnter(); };

  return (
    <div className="sc-root sc-no-scrollbar" style={{ minHeight: "100vh", width: "100%", background: C.bg, color: C.cream, overflowX: "hidden", position: "relative" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500&display=swap');

        /* Hide page scrollbar on welcome — content still scrolls, just no
           chrome. WebKit (Chrome/Safari/Edge), Firefox, and legacy IE/Edge
           each need their own selector. Applied to html/body via :has so
           the parent document chrome is also hidden while welcome is up. */
        html:has(.sc-no-scrollbar), body:has(.sc-no-scrollbar) {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        html:has(.sc-no-scrollbar)::-webkit-scrollbar,
        body:has(.sc-no-scrollbar)::-webkit-scrollbar,
        .sc-no-scrollbar::-webkit-scrollbar { width: 0; height: 0; display: none; }
        .sc-no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }

        .sc-root, .sc-root * { box-sizing: border-box; }
        .sc-root h1, .sc-root h2, .sc-root h3, .sc-root p { margin: 0; }
        .sc-root button { font: inherit; cursor: pointer; }

        @keyframes sc-ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .sc-ticker-track { display: flex; white-space: nowrap; padding: 12px 0; animation: sc-ticker 60s linear infinite; }
        .sc-ticker-item { display: flex; align-items: center; gap: 12px; padding: 0 24px; font-size: 12px; letter-spacing: 0.08em; }

        @keyframes sc-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(74,222,128,0.5); }
          50% { opacity: 0.6; box-shadow: 0 0 0 4px rgba(74,222,128,0); }
        }
        .sc-pulse-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; animation: sc-pulse 2s infinite; }
        @keyframes sc-blink { 50% { opacity: 0; } }
        .sc-blink { animation: sc-blink 1s step-end infinite; }

        @keyframes sc-reveal { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .sc-r1 { animation: sc-reveal 0.8s 0.1s both; }
        .sc-r2 { animation: sc-reveal 0.8s 0.25s both; }
        .sc-r3 { animation: sc-reveal 0.8s 0.4s both; }
        .sc-r4 { animation: sc-reveal 0.8s 0.55s both; }
        .sc-r5 { animation: sc-reveal 0.8s 0.7s both; }

        .sc-term-bar { display: flex; align-items: center; justify-content: space-between; padding: 8px 24px; }
        .sc-term-left, .sc-term-right { display: flex; align-items: center; gap: 24px; }
        .sc-term-group { display: flex; align-items: center; gap: 8px; }
        .sc-term-md, .sc-term-lg, .sc-term-sm { display: none; }
        @media (min-width: 640px) { .sc-term-sm { display: inline; } }
        @media (min-width: 768px) { .sc-term-md { display: flex; } }
        @media (min-width: 1024px) { .sc-term-lg { display: flex; } }
        .sc-hdr-md { display: none; }
        @media (min-width: 480px) { .sc-hdr-md { display: inline; } }

        .sc-hero { padding: 64px 24px 80px; position: relative; }
        .sc-hero-inner { max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 48px; align-items: center; }
        .sc-hero-inner > * { min-width: 0; }
        @media (min-width: 768px) { .sc-hero { padding: 96px 48px 80px; } }
        @media (min-width: 1024px) {
          .sc-hero { padding: 96px 80px 80px; }
          .sc-hero-inner { grid-template-columns: 2fr 3fr; }
        }

        .sc-eyebrow { display: flex; align-items: center; gap: 12px; margin-bottom: 32px; }
        .sc-eyebrow-line { height: 1px; width: 48px; background: ${C.cheddar}; }
        .sc-eyebrow-text { font-family: ${MONO}; font-size: 11px; letter-spacing: 0.3em; color: ${C.cheddar}; }

        .sc-headline { font-family: ${SERIF}; line-height: 0.9; margin-bottom: 32px; }
        .sc-headline span {
          display: block; font-size: clamp(3rem, 8vw, 7rem);
          text-shadow: 0 0 40px rgba(245,165,36,0.25), 0 0 80px rgba(245,165,36,0.1);
        }
        .sc-headline .sc-italic { font-style: italic; color: ${C.cheddarBright}; }

        .sc-lede { max-width: 520px; font-family: ${SANS}; font-size: 16px; line-height: 1.6; color: ${C.mutedBright}; margin-bottom: 40px; }

        .sc-cta-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
        .sc-btn {
          padding: 16px 32px; font-family: ${MONO}; font-size: 13px; letter-spacing: 0.2em;
          border: 1px solid ${C.borderBright}; background: transparent; color: ${C.cream};
          transition: background 0.2s; position: relative; overflow: hidden;
        }
        .sc-btn:hover { background: rgba(255,255,255,0.05); }
        .sc-btn-primary { background: ${C.cheddar}; border-color: ${C.cheddar}; color: ${C.bg}; font-weight: 600; }
        .sc-btn-primary:hover { background: ${C.cheddarBright}; }
        .sc-btn-primary::after {
          content: ""; position: absolute; top: 0; left: -100%; width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,209,102,0.4), transparent);
          transition: left 0.6s;
        }
        .sc-btn-primary:hover::after { left: 100%; }
        .sc-btn-ghost { background: transparent; border-color: ${C.border}; color: ${C.mutedBright}; }
        .sc-btn-ghost:hover { border-color: ${C.cheddar}; color: ${C.cheddarBright}; background: transparent; }

        .sc-status-row { margin-top: 48px; display: flex; align-items: center; gap: 8px; font-family: ${MONO}; font-size: 11px; color: ${C.muted}; flex-wrap: wrap; }
        .sc-sep { margin: 0 8px; }

        .sc-chart-panel {
          position: relative; border: 1px solid ${C.border};
          background: linear-gradient(180deg, ${C.panel} 0%, ${C.bg} 100%);
          aspect-ratio: 4 / 3;
        }
        .sc-chart-header {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; row-gap: 4px;
          padding: 8px 16px; border-bottom: 1px solid ${C.border};
          font-family: ${MONO}; font-size: 10px; color: ${C.muted};
        }
        .sc-chart-body { position: relative; height: calc(100% - 28px); }
        .sc-chart-overlay-tl { position: absolute; top: 16px; left: 16px; font-family: ${MONO}; font-size: 10px; display: flex; flex-direction: column; gap: 4px; }
        .sc-chart-overlay-tr { position: absolute; top: 16px; right: 16px; text-align: right; font-family: ${MONO}; font-size: 10px; }
        .sc-chart-overlay-b { position: absolute; bottom: 16px; left: 16px; right: 16px; display: flex; align-items: center; justify-content: space-between; font-family: ${MONO}; font-size: 9px; color: ${C.muted}; }

        .sc-chart-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: ${C.border}; margin-top: 1px; }
        .sc-chart-stat { background: ${C.panel}; padding: 12px; }
        .sc-chart-stat-label { font-family: ${MONO}; font-size: 9px; letter-spacing: 0.15em; color: ${C.muted}; margin-bottom: 4px; }
        .sc-chart-stat-value { font-family: ${MONO}; font-size: 14px; }

        .sc-section { padding: 80px 24px; border-top: 1px solid ${C.border}; }
        @media (min-width: 768px) { .sc-section { padding: 80px 48px; } }
        @media (min-width: 1024px) { .sc-section { padding: 80px 80px; } }
        .sc-section-inner { max-width: 1400px; margin: 0 auto; }

        .sc-section-head { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 64px; flex-wrap: wrap; gap: 24px; }
        .sc-section-title { font-family: ${SERIF}; font-size: clamp(2rem, 4vw, 3rem); line-height: 1.15; max-width: 640px; color: ${C.cream}; }
        .sc-section-title em { color: ${C.cheddarBright}; }

        .sc-feature-grid { display: grid; grid-template-columns: 1fr; gap: 1px; background: ${C.border}; }
        @media (min-width: 768px) { .sc-feature-grid { grid-template-columns: 1fr 1fr; } }
        @media (min-width: 1024px) { .sc-feature-grid { grid-template-columns: repeat(3, 1fr); } }

        .sc-feature-card {
          position: relative; padding: 24px; background: ${C.panel};
          border: 1px solid ${C.border}; transition: border-color 0.3s, transform 0.3s; cursor: pointer;
        }
        .sc-feature-card:hover { border-color: ${C.cheddar}; transform: translateY(-4px); }
        .sc-corner { position: absolute; width: 12px; height: 12px; }
        .sc-corner-tl { top: 0; left: 0; border-top: 1px solid ${C.cheddar}; border-left: 1px solid ${C.cheddar}; }
        .sc-corner-tr { top: 0; right: 0; border-top: 1px solid ${C.cheddar}; border-right: 1px solid ${C.cheddar}; }
        .sc-corner-bl { bottom: 0; left: 0; border-bottom: 1px solid ${C.cheddar}; border-left: 1px solid ${C.cheddar}; }
        .sc-corner-br { bottom: 0; right: 0; border-bottom: 1px solid ${C.cheddar}; border-right: 1px solid ${C.cheddar}; }

        .sc-feature-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px; }
        .sc-feature-tag { font-size: 10px; letter-spacing: 0.15em; }
        .sc-feature-dot { width: 6px; height: 6px; border-radius: 50%; }
        .sc-feature-title { font-size: 22px; line-height: 1.15; margin-bottom: 12px; font-style: italic; }
        .sc-feature-desc { font-size: 13px; line-height: 1.6; margin-bottom: 24px; }
        .sc-feature-foot { display: flex; align-items: flex-end; justify-content: space-between; padding-top: 16px; }
        .sc-metric-label { font-size: 9px; letter-spacing: 0.15em; margin-bottom: 4px; }
        .sc-metric { font-size: 24px; font-weight: 500; }
        .sc-feature-arrow { font-size: 14px; opacity: 0; transition: opacity 0.3s; }
        .sc-feature-card:hover .sc-feature-arrow { opacity: 1; }

        .sc-stats { display: grid; grid-template-columns: 1fr; gap: 32px; }
        @media (min-width: 768px) { .sc-stats { grid-template-columns: repeat(4, 1fr); gap: 48px; } }
        .sc-stat { border-left: 1px solid ${C.cheddar}; padding-left: 24px; }
        .sc-stat-num { font-family: ${SERIF}; font-size: 48px; font-style: italic; color: ${C.cheddarBright}; margin-bottom: 8px; line-height: 1; }
        .sc-stat-label { font-family: ${MONO}; font-size: 10px; letter-spacing: 0.15em; color: ${C.muted}; }

        .sc-cta-section { padding: 96px 24px; position: relative; overflow: hidden; border-top: 1px solid ${C.border}; }
        @media (min-width: 768px) { .sc-cta-section { padding: 96px 48px; } }
        .sc-cta-inner { max-width: 1000px; margin: 0 auto; text-align: center; position: relative; z-index: 1; }
        .sc-cta-headline { font-family: ${SERIF}; font-size: clamp(2.5rem, 6vw, 5rem); line-height: 0.95; color: ${C.cream}; margin-bottom: 32px; }
        .sc-cta-headline em { color: ${C.cheddarBright}; }
        .sc-cta-glow { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at center bottom, rgba(138,94,22,0.3) 0%, transparent 70%); }

        .sc-footer { padding: 32px 24px; border-top: 1px solid ${C.border}; background: ${C.panel}; font-family: ${MONO}; font-size: 11px; color: ${C.muted}; }
        @media (min-width: 768px) { .sc-footer { padding: 32px 48px; } }
        .sc-footer-inner { max-width: 1400px; margin: 0 auto; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 16px; }
        .sc-footer-links { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
        .sc-footer-link { background: transparent; border: none; padding: 0; color: ${C.muted}; font: inherit; letter-spacing: inherit; cursor: pointer; transition: color 0.2s; }
        .sc-footer-link:hover { color: ${C.cheddar}; }
        .sc-wordmark { font-family: ${SERIF}; font-style: italic; font-size: 18px; color: ${C.cheddar}; }

        .sc-hero-glow { position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 1200px; height: 800px; pointer-events: none; opacity: 0.4; background: radial-gradient(ellipse at center, rgba(138,94,22,0.3) 0%, transparent 60%); }

        /* ============ MODAL / GATE ============ */
        .sc-modal-backdrop {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
          animation: sc-fade-in 0.2s ease-out;
        }
        @keyframes sc-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .sc-modal {
          background: ${C.panel};
          border: 1px solid ${C.cheddar};
          box-shadow: 0 0 60px rgba(245, 165, 36, 0.15), 0 20px 60px rgba(0,0,0,0.6);
          max-width: 100%;
          display: flex; flex-direction: column;
          animation: sc-modal-in 0.25s ease-out;
        }
        @keyframes sc-modal-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .sc-modal-gate { width: 480px; }
        .sc-modal-legal { width: 820px; max-height: 85vh; }
        .sc-modal-head {
          display: flex; align-items: center; gap: 10px;
          padding: 14px 20px;
          border-bottom: 1px solid ${C.border};
          background: ${C.bg};
        }
        .sc-modal-x {
          background: transparent; border: none; color: ${C.muted};
          font-size: 22px; line-height: 1; padding: 0 4px;
          transition: color 0.15s;
        }
        .sc-modal-x:hover { color: ${C.cream}; }
        .sc-modal-body {
          padding: 24px 28px;
          overflow-y: auto;
          font-family: ${SANS};
          color: ${C.mutedBright};
          font-size: 13px; line-height: 1.65;
        }

        /* ============ GATE FORM ============ */
        .sc-gate-body { padding: 24px 28px 28px; display: flex; flex-direction: column; gap: 14px; }
        .sc-gate-lede { font-family: ${SANS}; font-size: 13px; color: ${C.mutedBright}; line-height: 1.5; }
        .sc-gate-label { font-family: ${MONO}; font-size: 10px; letter-spacing: 0.2em; color: ${C.muted}; margin-top: 4px; }
        .sc-gate-input {
          background: ${C.bg};
          border: 1px solid ${C.borderBright};
          color: ${C.cream};
          font-family: ${MONO}; font-size: 14px; letter-spacing: 0.1em;
          padding: 12px 14px;
          outline: none;
          transition: border-color 0.15s;
        }
        .sc-gate-input:focus { border-color: ${C.cheddar}; }
        .sc-gate-code { font-size: 22px; letter-spacing: 0.5em; text-align: center; }
        .sc-gate-links {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 4px; font-family: ${SANS}; font-size: 12px;
        }
        .sc-gate-links .sc-gate-link:disabled { color: ${C.muted}; cursor: default; text-decoration: none; }
        .sc-gate-check {
          display: flex; align-items: flex-start; gap: 10px;
          font-family: ${SANS}; font-size: 12px; color: ${C.mutedBright}; line-height: 1.5;
          cursor: pointer; margin-top: 4px;
        }
        .sc-gate-check input {
          margin-top: 2px;
          accent-color: ${C.cheddar};
          width: 14px; height: 14px;
          flex-shrink: 0;
        }
        .sc-gate-link {
          background: transparent; border: none; padding: 0;
          color: ${C.cheddar}; font: inherit;
          text-decoration: underline; text-underline-offset: 2px;
          cursor: pointer;
        }
        .sc-gate-link:hover { color: ${C.cheddarBright}; }
        .sc-gate-err {
          font-family: ${MONO}; font-size: 11px; color: ${C.sell};
          padding: 8px 12px; border-left: 2px solid ${C.sell}; background: rgba(239, 68, 68, 0.08);
        }
        .sc-gate-foot {
          margin-top: 8px; padding-top: 14px; border-top: 1px solid ${C.border};
          font-family: ${MONO}; font-size: 10px; color: ${C.muted}; letter-spacing: 0.1em; text-align: center;
        }

        /* ============ LEGAL MARKDOWN ============ */
        .sc-legal-h1 { font-family: ${SERIF}; font-size: 28px; color: ${C.cheddarBright}; margin: 4px 0 16px; font-style: italic; }
        .sc-legal-h2 { font-family: ${SERIF}; font-size: 20px; color: ${C.cream}; margin: 24px 0 10px; }
        .sc-legal-h3 { font-family: ${MONO}; font-size: 12px; letter-spacing: 0.15em; color: ${C.cheddar}; margin: 16px 0 8px; text-transform: uppercase; }
        .sc-legal-p { margin: 0 0 12px; }
        .sc-legal-ul { margin: 0 0 12px; padding-left: 20px; }
        .sc-legal-ul li { margin-bottom: 4px; }
        .sc-legal-hr { border: 0; border-top: 1px solid ${C.border}; margin: 20px 0; }
        .sc-legal-quote {
          border-left: 2px solid ${C.cheddar};
          padding: 10px 14px;
          background: rgba(245, 165, 36, 0.06);
          margin: 0 0 14px;
          font-size: 12px;
          color: ${C.cream};
        }
        .sc-legal-quote strong { color: ${C.cheddarBright}; }
      `}</style>

      <div className="sc-root">
        <TerminalBar />
        <TickerTape />

        <section className="sc-hero">
          <div className="sc-hero-glow" />
          <div className="sc-hero-inner">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="sc-eyebrow sc-r1">
                <div className="sc-eyebrow-line" />
                <span className="sc-eyebrow-text">SOLANA / ORDERFLOW / 2026</span>
              </div>

              <h1 className="sc-headline sc-r2">
                <span>Read the</span>
                <span className="sc-italic">tape.</span>
                <span>Stack the</span>
                <span className="sc-italic">cheddar.</span>
              </h1>

              <p className="sc-lede sc-r3">
                A trading terminal built for Solana memecoins. Footprint charts, live orderflow,
                position tagging, and an autonomous execution bot—all streaming at block speed.
                No aggregators. No delay. Just the tape.
              </p>

              <div className="sc-cta-row sc-r4">
                <button className="sc-btn sc-btn-primary" onClick={handleEnterClick}>
                  ENTER TERMINAL →
                </button>
                <button className="sc-btn sc-btn-ghost" onClick={handleEnterClick}>
                  JOIN THE WAITLIST
                </button>
              </div>
              <p className="sc-r4" style={{ fontFamily: MONO, fontSize: 11, color: C.muted, marginTop: 12 }}>
                Beta is allowlist-only. Enter your email—if you're not on the list yet, you can grab a waitlist spot.
              </p>

              <div className="sc-status-row sc-r5">
                <span style={{ color: C.buy }}>●</span>
                <span>NLN gRPC direct</span>
                <span className="sc-sep">│</span>
                <span>Jupiter Ultra execution</span>
                <span className="sc-sep">│</span>
                <span>non-custodial</span>
                <span className="sc-blink" style={{ color: C.cheddar, marginLeft: 4 }}>_</span>
              </div>
            </div>

            <div className="sc-r3">
              <div className="sc-chart-panel">
                <div className="sc-chart-header">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: C.cheddar }}>◆</span>
                    <span style={{ color: C.cream }}>ROULETTE_WALK</span>
                    <span className="sc-hdr-md">│</span><span className="sc-hdr-md">EU WHEEL</span><span className="sc-hdr-md">│</span><span className="sc-hdr-md">20 SPINS/CANDLE</span><span>│</span><span style={{ color: C.cheddarBright }}>MA9</span>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.sell }} />
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.cheddar }} />
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.buy }} />
                  </div>
                </div>

                <div className="sc-chart-body">
                  <RouletteCanvas />

                  <div className="sc-chart-overlay-b">
                    <span>HOUSE EDGE: <span style={{ color: C.sell }}>−2.7%</span> EVERY SPIN</span>
                  </div>
                </div>
              </div>

              <p style={{ fontFamily: MONO, fontSize: 12, color: C.mutedBright, marginTop: 16, lineHeight: 1.7 }}>
                That chart? Coin flips with a house cut. And that MA9 line still looks like trend
                confirmation—your brain finds patterns in pure noise. The tape is different: real
                aggressors, real liquidity, cell-by-cell buy vs sell at every price. The market
                leaves footprints. We read them.
              </p>
            </div>
          </div>
        </section>

        <section className="sc-section">
          <div className="sc-section-inner">
            <div className="sc-section-head">
              <div>
                <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.3em", color: C.cheddar, marginBottom: 16 }}>
                  ── MODULES
                </div>
                <h2 className="sc-section-title">
                  Every tool a Solana scalper <em>actually</em> needs.
                </h2>
              </div>
              <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.15em", color: C.muted }}>
                06 MODULES // RAW TAPE // 0 AGGREGATORS
              </div>
            </div>

            <div className="sc-feature-grid">
              <FeatureCard id="01" tag="CHART" title="Footprint charting"
                desc="Cell-by-cell buy vs sell volume at every price level. See exactly where the aggressors stepped in."
                metric="CELL" metricLabel="LEVEL RESOLUTION" accent={C.buy} />
              <FeatureCard id="02" tag="TAPE" title="Live trade tape"
                desc="Every swap, every LP, every sniper—streamed from NLN gRPC with wick-eligible filtering."
                metric="gRPC" metricLabel="STRAIGHT OFF CHAIN" accent={C.cheddar} />
              <FeatureCard id="03" tag="BOT" title="Orderflow bot"
                desc="Autonomous execution driven by Tape Heat, Churn, Approach Velocity, and Liquidity Reactivity. Exits when the reasons die, not just when price moves."
                metric="24/7" metricLabel="AUTO MODE" accent={C.sell} />
              <FeatureCard id="04" tag="SCAN" title="Token scanner"
                desc="Organic scoring, anti-wash filters, DexScreener metadata. Find runners before they run."
                metric="LIVE" metricLabel="WS STREAM" accent={C.cheddarBright} />
              <FeatureCard id="05" tag="SWAP" title="Jupiter execution"
                desc="Ultra API relayer swaps. Precise entry mcap via Helius parsed transactions, post-buy refinement."
                metric="ULTRA" metricLabel="JUPITER RELAYER" accent={C.buy} />
              <FeatureCard id="06" tag="DATA" title="Brokers & exchanges"
                desc="Footprint and orderflow tooling across the venues you already use. Your credentials, your accounts."
                metric="OWN" metricLabel="YOUR KEYS" accent={C.cheddar} />
            </div>
          </div>
        </section>

        <section className="sc-section" style={{ background: `linear-gradient(180deg, ${C.bg} 0%, ${C.panel} 100%)` }}>
          <div className="sc-section-inner">
            <div className="sc-stats">
              {[
                { num: "0", label: "AGGREGATORS IN THE PATH" },
                { num: "100%", label: "SELF-CUSTODY — YOUR KEYS" },
                { num: "1s", label: "CANDLES DOWN TO SECONDS" },
                { num: "∞", label: "CHEDDAR TO STACK" },
              ].map((s) => (
                <div key={s.label} className="sc-stat">
                  <div className="sc-stat-num">{s.num}</div>
                  <div className="sc-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sc-section">
          <div className="sc-section-inner">
            <div className="sc-section-head">
              <div>
                <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.3em", color: C.cheddar, marginBottom: 16 }}>
                  ── THE TAPE
                </div>
                <h2 className="sc-section-title">
                  This is what <em>edge</em> looks like.
                </h2>
              </div>
              <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.15em", color: C.muted }}>
                BUY VS SELL // EVERY PRICE LEVEL // BLOCK SPEED
              </div>
            </div>

            <div className="sc-chart-panel" style={{ aspectRatio: "auto" }}>
              <div className="sc-chart-header">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: C.cheddar }}>◆</span>
                  <span style={{ color: C.cream }}>FOOTPRINT_CHART</span>
                  <span className="sc-hdr-md">│</span><span className="sc-hdr-md">SIM FEED</span><span>│</span><span>5s</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.sell }} />
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.cheddar }} />
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.buy }} />
                </div>
              </div>
              <div className="sc-chart-body" style={{ height: 240 }}>
                <FootprintCanvas />

                <div className="sc-chart-overlay-tl">
                  <div style={{ color: C.muted }}>MCAP <span style={{ color: C.cream }}>$2.34B</span></div>
                  <div style={{ color: C.muted }}>VOL24 <span style={{ color: C.cream }}>$847M</span></div>
                  <div style={{ color: C.muted }}>Δ <span style={{ color: C.buy }}>+12,484</span></div>
                </div>

                <div className="sc-chart-overlay-tr">
                  <div style={{ color: C.cheddarBright, fontSize: "1.1rem" }}>2.3451</div>
                  <div style={{ color: C.sell }}>▼ 4.23%</div>
                </div>

                <div className="sc-chart-overlay-b">
                  <span>BID/ASK IMBALANCE: <span style={{ color: C.cream }}>0.68</span></span>
                  <span className="sc-blink" style={{ color: C.buy }}>● LIVE</span>
                </div>
              </div>
            </div>

            <div className="sc-chart-stats">
              {[
                ["SOURCE", "gRPC", C.buy],
                ["EXECUTION", "JUP ULTRA", C.cheddar],
                ["CUSTODY", "SELF", C.cream],
                ["AGGREGATORS", "0", C.cheddar],
              ].map(([label, value, color]) => (
                <div key={label} className="sc-chart-stat">
                  <div className="sc-chart-stat-label">{label}</div>
                  <div className="sc-chart-stat-value" style={{ color }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sc-cta-section">
          <div className="sc-cta-glow" />
          <div className="sc-cta-inner">
            <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.3em", color: C.cheddar, marginBottom: 24 }}>
              ── v2.4.1 // PUBLIC BETA
            </div>
            <h2 className="sc-cta-headline">
              The market leaves <em>footprints.</em>
              <br />We read them.
            </h2>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                className="sc-btn sc-btn-primary"
                onClick={handleEnterClick}
                style={{ padding: "20px 48px", fontSize: 13, letterSpacing: "0.3em" }}
              >
                LAUNCH CHEDDAR →
              </button>
              <button
                className="sc-btn sc-btn-ghost"
                onClick={handleEnterClick}
                style={{ padding: "20px 48px", fontSize: 13, letterSpacing: "0.3em" }}
              >
                JOIN THE WAITLIST
              </button>
            </div>
          </div>
        </section>

        <footer className="sc-footer">
          <div className="sc-footer-inner">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="sc-wordmark">Cheddar Terminal</span>
              <span>© 2026</span>
            </div>
            <div className="sc-footer-links">
              <button className="sc-footer-link" onClick={() => setLegalDoc("terms")}>TERMS</button>
              <button className="sc-footer-link" onClick={() => setLegalDoc("risk")}>RISK</button>
              <button className="sc-footer-link" onClick={() => setLegalDoc("disclaimer")}>DISCLAIMER</button>
                          </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="sc-pulse-dot" style={{ width: 6, height: 6, background: C.buy }} />
              <span>PUBLIC BETA</span>
            </div>
          </div>
        </footer>
      </div>

      {gateOpen && (
        <AccessGate
          onSuccess={handleGateSuccess}
          onClose={() => setGateOpen(false)}
          openLegal={setLegalDoc}
        />
      )}
      {legalDoc && (
        <LegalModal docKey={legalDoc} onClose={() => setLegalDoc(null)} />
      )}
    </div>
  );
}